<template>
  <div class="row mt-4">
    <!-- Una sola tarjeta: título, filtros, tabla izquierda, gráfico derecha -->
    <div class="col-12">
      <div class="card p-3">
        <h5 class="mb-3 section-title card-section-title">Informe de Hornos</h5>

        <!-- Filtros: buscador izquierda, fechas derecha (mismo tamaño y posición en todas las tarjetas) -->
        <div class="card-filters-row d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
          <div class="card-filters-buscar">
            <label for="buscar_hornos" class="form-label">Buscar</label>
            <input
              id="buscar_hornos"
              type="text"
              class="form-control"
              v-model="buscar"
              placeholder="Horno, consecutivo o quema..."
            />
          </div>
          <div class="card-filters-fechas d-flex flex-wrap align-items-end gap-5">
            <div class="card-filters-fecha">
              <label for="fecha_inicio" class="form-label">Fecha inicio</label>
              <input id="fecha_inicio" type="date" class="form-control" v-model="fecha_inicio" />
            </div>
            <div class="card-filters-fecha">
              <label for="fecha_fin" class="form-label">Fecha fin</label>
              <input id="fecha_fin" type="date" class="form-control" v-model="fecha_fin" />
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Izquierda: tabla Detalle de Cargas por Horno -->
          <div class="col-lg-6">
            <div class="table-responsive">
              <table class="table table-sm text-center table-informe-hornos">
                <thead>
                  <tr>
                    <th class="text-center">ID</th>
                    <th class="text-center">Horno</th>
                    <th class="text-center">Consecutivo</th>
                    <th class="text-center">Quemada</th>
                    <th class="text-center">Carbon Total (kg)</th>
                    <th class="text-center">Cargas</th>
                    <th class="text-center">Prom. Absorción Agua</th>
                    <th class="text-center">Fin</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in tablaHornosRows" :key="item ? item.id : `empty-${idx}`" :class="{ 'fila-vacia': !item }">
                    <td class="text-center">{{ item ? item.id : '—' }}</td>
                    <td class="text-center">{{ item ? (item.horno ?? '—') : '—' }}</td>
                    <td class="text-center">{{ item ? (item.consecutivo ?? '—') : '—' }}</td>
                    <td class="text-center">{{ item ? (item.burn_number ?? '—') : '—' }}</td>
                    <td class="text-center">{{ item && item.total_carbon != null ? item.total_carbon.toFixed(2) : '—' }}</td>
                    <td class="text-center">{{ item ? (item.cargas ?? '—') : '—' }}</td>
                    <td class="text-center">{{ item && item.promedio_absorcion_agua != null ? item.promedio_absorcion_agua.toFixed(2) : '—' }}</td>
                    <td class="text-center">{{ item ? formatDate(item.fecha_finalizacion) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="hornos-pagination d-flex justify-content-center align-items-center mt-2 gap-2">
              <button
                type="button"
                class="hornos-pagination-btn"
                @click="prevPageHornos"
                :disabled="currentPage === 1"
              >
                &lt;&lt;
              </button>
              <span class="hornos-pagination-text">{{ currentPage }}/{{ totalPages }}</span>
              <button
                type="button"
                class="hornos-pagination-btn"
                @click="nextPageHornos"
                :disabled="currentPage >= totalPages"
              >
                &gt;&gt;
              </button>
            </div>
          </div>

          <!-- Derecha: gráfico y resumen -->
          <div class="col-lg-6 hornos-chart-col">
            <div class="hornos-chart-wrap">
              <canvas ref="hornosChart"></canvas>
            </div>
            <div class="hornos-resumen-line d-flex flex-wrap align-items-center justify-content-center gap-3 text-center">
              <span><strong>Total de Hornos:</strong> {{ resumen.total_hornos }}</span>
              <span><strong>Rango:</strong> {{ resumen.rango.desde || "—" }} → {{ resumen.rango.hasta || "—" }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla Detalle de Cargas -->
    <div class="col-12 mt-4">
      <div class="card p-3">
        <h5 class="mb-3 section-title card-section-title">Detalle de quemas</h5>

        <div class="card-filters-row d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
          <div class="card-filters-buscar">
            <label for="buscar_quemas" class="form-label">Buscar</label>
            <input
              id="buscar_quemas"
              type="text"
              class="form-control"
              v-model="buscar_quemas"
              placeholder="Buscar por horno o patio carbón..."
            />
          </div>
          <div class="card-filters-fechas d-flex flex-wrap align-items-end gap-5">
            <div class="card-filters-fecha">
              <label for="fecha_inicio_quemas" class="form-label">Fecha inicio</label>
              <input id="fecha_inicio_quemas" type="date" class="form-control" v-model="fecha_inicio_quemas" />
            </div>
            <div class="card-filters-fecha">
              <label for="fecha_fin_quemas" class="form-label">Fecha fin</label>
              <input id="fecha_fin_quemas" type="date" class="form-control" v-model="fecha_fin_quemas" />
            </div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered table-sm table-quemas">
            <thead>
              <tr>
                <th class="text-center">🔥 Horno</th>
                <th class="text-center">🪵 Patio Carbón</th>
                <th class="text-center">⚖️ Cantidad (kg)</th>
                <th class="text-center">💧 Absorción Agua</th>
                <th class="text-center">💧² Absorción Agua 2</th>
                <th class="text-center"><i class="fa fa-calendar text-success me-1" aria-hidden="true"></i>Inicio</th>
                <th class="text-center"><i class="fa fa-calendar text-danger me-1" aria-hidden="true"></i>Fin</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="detalleQuemasFilas.length">
                <tr v-for="(row, idx) in detalleQuemasPagina" :key="row ? `q-${idx}-${row.horno}` : `empty-q-${idx}`" :class="{ 'fila-vacia': !row }">
                  <td class="text-center">{{ row ? row.horno : '—' }}</td>
                  <td class="text-center">{{ row ? row.patio_carbon : '—' }}</td>
                  <td class="text-center">{{ row && row.carbon_quantity != null ? row.carbon_quantity : '—' }}</td>
                  <td class="text-center">{{ row && row.water_absorption != null ? row.water_absorption : '—' }}</td>
                  <td class="text-center">{{ row && row.water_absorption_2 != null ? row.water_absorption_2 : '—' }}</td>
                  <td class="text-center">{{ row ? formatDate(row.fecha_inicio) : '—' }}</td>
                  <td class="text-center">{{ row ? formatDate(row.fecha_fin) : '—' }}</td>
                </tr>
              </template>
              <tr v-else>
                <td colspan="7" class="text-center text-muted">No se encontraron resultados</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="hornos-pagination d-flex justify-content-center align-items-center mt-2 gap-2">
          <button
            type="button"
            class="hornos-pagination-btn"
            @click="prevPageQuemas"
            :disabled="currentPageQuemas === 1"
          >
            &lt;&lt;
          </button>
          <span class="hornos-pagination-text">{{ currentPageQuemas }}/{{ totalPagesQuemas }}</span>
          <button
            type="button"
            class="hornos-pagination-btn"
            @click="nextPageQuemas"
            :disabled="currentPageQuemas >= totalPagesQuemas"
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "InformeHornos",
  data() {
    const hoy = new Date();
    const hace15 = new Date();
    hace15.setDate(hoy.getDate() - 15);

    const formatDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    return {
      fecha_inicio: formatDate(hace15),
      fecha_fin: formatDate(hoy),
      buscar: "",
      buscar_quemas: "",
      fecha_inicio_quemas: formatDate(hace15),
      fecha_fin_quemas: formatDate(hoy),
      resumen: { total_hornos: 0, rango: { desde: null, hasta: null } },
      totales: [],
      detalle: [],
      listaHornos: [], // todos los hornos (bodegas tipo 2) para el gráfico
      chartInstance: null,
      currentPage: 1,
      perPage: 6,
      currentPageQuemas: 1,
      perPageQuemas: 5,
    };
  },
  computed: {
    totalPages() {
      const total = this.totalesFiltrados.length;
      return Math.max(1, Math.ceil(total / this.perPage));
    },
    tablaHornosRows() {
      const data = this.totalesFiltrados;
      const start = (this.currentPage - 1) * this.perPage;
      const pageData = data.slice(start, start + this.perPage);
      const rows = [...pageData];
      while (rows.length < this.perPage) rows.push(null);
      return rows;
    },
    totalesFiltrados() {
      const q = (this.buscar || "").trim().toLowerCase();
      if (!q) return this.totales;
      return this.totales.filter((t) => {
        const horno = (t.horno ?? "").toString().toLowerCase();
        const consecutivo = (t.consecutivo ?? "").toString().toLowerCase();
        const quema = (t.burn_number ?? "").toString().toLowerCase();
        return horno.includes(q) || consecutivo.includes(q) || quema.includes(q);
      });
    },
    detalleQuemasFiltrado() {
      const q = (this.buscar_quemas || "").trim().toLowerCase();
      const fi = this.fecha_inicio_quemas || "";
      const ff = this.fecha_fin_quemas || "";

      return (this.detalle || [])
        .map((horno) => {
          const hornName = (horno.horno ?? "").toString().toLowerCase();
          const furnaces = (horno.furnaces || []).filter((f) => {
            const patio = (f.patio_carbon ?? "").toString().toLowerCase();
            const textOk = !q || hornName.includes(q) || patio.includes(q);

            const fechaRaw = (f.fecha_fin || f.fecha_inicio || "").toString();
            const fecha = fechaRaw ? fechaRaw.slice(0, 10) : "";
            const fromOk = !fi || (fecha && fecha >= fi);
            const toOk = !ff || (fecha && fecha <= ff);

            return textOk && fromOk && toOk;
          });

          return { ...horno, furnaces };
        })
        .filter((h) => h.furnaces && h.furnaces.length > 0);
    },
    // Filas aplanadas (horno + furnace) para Detalle de quemas
    detalleQuemasFilas() {
      return (this.detalleQuemasFiltrado || []).flatMap((h) =>
        (h.furnaces || []).map((f) => ({ horno: h.horno, ...f }))
      );
    },
    totalPagesQuemas() {
      const total = this.detalleQuemasFilas.length;
      return Math.max(1, Math.ceil(total / this.perPageQuemas));
    },
    detalleQuemasPagina() {
      const data = this.detalleQuemasFilas;
      const start = (this.currentPageQuemas - 1) * this.perPageQuemas;
      const pageData = data.slice(start, start + this.perPageQuemas);
      const rows = [...pageData];
      while (rows.length < this.perPageQuemas) rows.push(null);
      return rows;
    },
  },
  watch: {
    fecha_inicio() {
      this.fetchInforme();
    },
    fecha_fin() {
      this.fetchInforme();
    },
    buscar() {
      this.currentPage = 1;
    },
    // Gráfico actualizado al filtrar y al quitar el filtro (vuelve al original)
    totalesFiltrados: {
      handler() {
        this.$nextTick(() => this.renderChart());
      },
      deep: true,
    },
    buscar_quemas() {
      this.currentPageQuemas = 1;
    },
    fecha_inicio_quemas() {
      this.currentPageQuemas = 1;
    },
    fecha_fin_quemas() {
      this.currentPageQuemas = 1;
    },
  },
  methods: {
    prevPageHornos() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPageHornos() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    prevPageQuemas() {
      if (this.currentPageQuemas > 1) this.currentPageQuemas--;
    },
    nextPageQuemas() {
      if (this.currentPageQuemas < this.totalPagesQuemas) this.currentPageQuemas++;
    },
    async fetchInforme() {
      try {
        await this.cargarListaHornos();

        const params = {
          fecha_inicio: this.fecha_inicio,
          fecha_fin: this.fecha_fin,
        };

        const res = await this.$store.dispatch("analitycs/getInformeHornos", params);
        // Aceptar respuesta directa (res.data) o envuelta en res.data.data
        const data = res.data && typeof res.data.data !== 'undefined' ? res.data.data : (res.data || {});

        this.resumen = {
          total_hornos: data.total_hornos ?? 0,
          rango: data.rango || { desde: null, hasta: null },
        };
        this.totales = Array.isArray(data.totales) ? data.totales : [];
        this.detalle = Array.isArray(data.detalle) ? data.detalle : [];
        this.currentPage = 1;
        this.currentPageQuemas = 1;
        this.$nextTick(() => this.renderChart());
      } catch (error) {
        console.error("Error cargando informe de hornos:", error);
        this.totales = [];
        this.detalle = [];
        this.$nextTick(() => this.renderChart());
      }
    },

    async cargarListaHornos() {
      try {
        const data = await this.$store.dispatch("bodegas/getAll");
        const bodegas = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
        const hornos = bodegas.filter((b) => Number(b.tipo) === 2).map((b) => b.name || "");
        this.listaHornos = hornos.filter(Boolean).sort((a, b) => {
          const na = parseInt(a.replace(/\D/g, ""), 10) || 0;
          const nb = parseInt(b.replace(/\D/g, ""), 10) || 0;
          return na - nb || (a || "").localeCompare(b || "");
        });
      } catch (e) {
        this.listaHornos = [];
      }
    },

    renderChart(retry = 0) {
      const canvas = this.$refs.hornosChart;
      if (!canvas) {
        if (retry < 8) {
          this.$nextTick(() => setTimeout(() => this.renderChart(retry + 1), 100));
        }
        return;
      }

      const ctx = typeof canvas.getContext === "function" ? canvas.getContext("2d") : null;
      if (!ctx) return;

      const existingChart = Chart.getChart(canvas);
      if (existingChart) existingChart.destroy();
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      // Carbon total por horno (si hay filtro, se refleja; al limpiar, vuelve al original).
      const list = this.totalesFiltrados;
      const porHorno = {};
      list.forEach((t) => {
        const nombre = t.horno || `Horno #${t.id}`;
        if (!porHorno[nombre]) porHorno[nombre] = 0;
        porHorno[nombre] += Number(t.total_carbon) || 0;
      });

      // Mostrar todos los hornos (ocupados o no); si no existe catálogo, usar los que tengan datos.
      const labels = this.listaHornos.length
        ? [...this.listaHornos]
        : Object.keys(porHorno).sort((a, b) => {
            const na = parseInt((a || "").replace(/\D/g, ""), 10) || 0;
            const nb = parseInt((b || "").replace(/\D/g, ""), 10) || 0;
            return na - nb || (a || "").localeCompare(b || "");
          });
      const dataCarbon = labels.map((nombre) => porHorno[nombre] || 0);
      const totalCarbonHornos = dataCarbon.reduce((acc, val) => acc + (Number(val) || 0), 0);
      const labelsChart = labels.length ? [...labels, "Total hornos"] : ["Total hornos"];
      const dataCarbonChart = labels.length ? [...dataCarbon, totalCarbonHornos] : [0];

      // Un color por barra
      const colores = [
        "#4BC0C0",
        "#FF6384",
        "#FFCE56",
        "#9966FF",
        "#FF9F40",
        "#2ecc71",
        "#e74c3c",
        "#9b59b6",
      ];
      const backgroundColor = labelsChart.map((_, i) =>
        i === labelsChart.length - 1 ? "#36A2EB" : colores[i % colores.length]
      );

      try {
        this.chartInstance = new Chart(ctx, {
          type: "bar",
          data: {
            labels: labelsChart,
            datasets: [
              {
                label: "Carbon Total (kg)",
                data: dataCarbonChart,
                backgroundColor,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: { legend: { display: false } },
            scales: {
              x: {
                grid: {
                  display: false,
                  drawBorder: false,
                },
                ticks: {
                  autoSkip: true,
                  maxRotation: 45,
                  minRotation: 0,
                },
              },
              y: {
                beginAtZero: true,
                grid: {
                  display: true,
                  drawBorder: false,
                },
              },
            },
          },
        });
      } catch (err) {
        console.error("[InformeHornos] Error al dibujar gráfico:", err);
      }
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("es-ES");
    },
  },
  mounted() {
    this.fetchInforme();
    this.$nextTick(() => {
      setTimeout(() => this.renderChart(), 150);
    });
  },
};
</script>

<style scoped>
.card-section-title.section-title {
  font-weight: 800;
  color: #1f2d5a;
  letter-spacing: 0.6px;
}

.card-section-title.section-title::after {
  content: "";
  display: block;
  width: 44px;
  height: 3px;
  margin-top: 6px;
  border-radius: 999px;
  background: #f6a500;
}

.hornos-chart-col {
  min-height: 320px;
}
.hornos-chart-wrap {
  position: relative;
  height: 280px;
  min-height: 280px;
  width: 100%;
}
.hornos-chart-wrap canvas {
  max-height: 280px;
  width: 100% !important;
  height: 280px !important;
}

.hornos-resumen-line {
  margin-top: 0.75rem;
  margin-bottom: 0;
  padding-top: 0;
}

.table {
  font-size: 0.8rem;
}

/* Líneas muy suaves en la tabla Informe de Hornos */
.table-informe-hornos thead th,
.table-informe-hornos tbody td {
  border-color: rgba(0, 0, 0, 0.04) !important;
  border-bottom-width: 1px;
}
.table-informe-hornos tbody tr {
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}
.table-informe-hornos thead th {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* Guiones "—" de celdas vacías muy suaves */
.table-informe-hornos tbody tr.fila-vacia td {
  color: rgba(0, 0, 0, 0.12);
}

.table-bordered td,
.table-bordered th {
  border: 1px solid #ddd;
}

/* Detalle de quemas: líneas muy suaves y guiones suaves en filas vacías */
.table-quemas thead th,
.table-quemas tbody td {
  border-color: rgba(0, 0, 0, 0.04) !important;
  border-width: 1px;
}
.table-quemas thead th {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.table-quemas tbody tr.fila-vacia td {
  color: rgba(0, 0, 0, 0.12);
}

.hornos-pagination {
  gap: 0.5rem;
}

.hornos-pagination-btn {
  min-width: 28px;
  height: 26px;
  padding: 0 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1f2d5a;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hornos-pagination-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #1f2d5a;
}

.hornos-pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hornos-pagination-text {
  font-size: 0.85rem;
  color: #1f2d5a;
  min-width: 2rem;
  text-align: center;
}
</style>
